
import { defineComponent, PropType, ref, watch } from 'vue';
import { FormModalShell } from '@/components/shared/modal';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import {
  defaultGiaTriForLoai,
  loaiGiaTriLabel,
  normalizeCaiDatItem,
} from '@/core/helpers/cai-dat';
import { swalErrNotification } from '@/core/helpers/utils';
import { CaiDatService } from '@/core/services/CaiDat.service';
import { ICaiDatItem } from '@/core/interfaces/ApiResponses';
import CaiDatGiaTriControl from './CaiDatGiaTriControl.vue';
import Swal from 'sweetalert2';

export default defineComponent({
  name: 'cai-dat-edit-modal',

  components: {
    FormModalShell,
    ButtonCustom,
    CaiDatGiaTriControl,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object as PropType<ICaiDatItem | null>,
      default: null,
    },
  },

  emits: ['close', 'success'],

  setup(props, { emit }) {
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();
    const formRef = ref<null | HTMLFormElement>(null);
    const saving = ref(false);
    const form = ref({
      gia_tri: '',
      ghi_chu: '',
    });
    const original = ref({
      gia_tri: '',
      ghi_chu: '',
    });

    const rules = {
      gia_tri: [
        {
          validator: (
            _rule: unknown,
            _value: string,
            callback: (e?: Error) => void
          ) => {
            const hasGiaTri =
              form.value.gia_tri !== null &&
              form.value.gia_tri !== undefined &&
              String(form.value.gia_tri).trim() !== '';
            const hasGhiChu = !!form.value.ghi_chu?.trim();
            if (!hasGiaTri && !hasGhiChu) {
              callback(new Error('Cần ít nhất giá trị hoặc ghi chú'));
              return;
            }
            callback();
          },
          trigger: 'blur',
        },
      ],
    };

    watch(
      () => [props.isOpen, props.item] as const,
      ([open, item]) => {
        if (!open || !item) return;

        const normalized = normalizeCaiDatItem(item);
        const giaTri =
          normalized.gia_tri ??
          defaultGiaTriForLoai(normalized.loai_gia_tri);
        const ghiChu = normalized.ghi_chu ?? '';

        form.value = {
          gia_tri: giaTri,
          ghi_chu: ghiChu,
        };
        original.value = {
          gia_tri: giaTri,
          ghi_chu: ghiChu,
        };
        (formRef.value as any)?.clearValidate?.();
      }
    );

    const onClose = () => {
      emit('close');
    };

    const submit = () => {
      if (!formRef.value || !props.item) return;

      (formRef.value as any).validate(async (valid: boolean) => {
        if (!valid || !props.item) return;

        const nextGiaTri = String(form.value.gia_tri ?? '');
        const nextGhiChu = form.value.ghi_chu;
        const payload: { gia_tri?: string; ghi_chu?: string } = {};

        if (nextGiaTri !== original.value.gia_tri) {
          payload.gia_tri = nextGiaTri;
        }
        if (nextGhiChu !== original.value.ghi_chu) {
          payload.ghi_chu = nextGhiChu;
        }

        if (!Object.keys(payload).length) {
          emit('close');
          return;
        }

        saving.value = true;
        try {
          await CaiDatService.update(props.item.id, payload);

          await Swal.fire({
            icon: 'success',
            title: 'Thành công',
            text: 'Đã cập nhật cài đặt',
            timer: 1500,
            showConfirmButton: false,
          });

          emit('success');
          emit('close');
        } catch (error) {
          await swalErrNotification(error, 'Có lỗi khi cập nhật cài đặt');
        } finally {
          saving.value = false;
        }
      });
    };

    return {
      ButtonsType,
      ButtonTypeColors,
      formRef,
      form,
      rules,
      saving,
      loaiGiaTriLabel,
      onClose,
      submit,
    };
  },
});
